import { useKinds } from "@certa/queries/hooks/kinds.hooks";
import { useCheckPermission } from "@certa/common/utils/permissions/Chowkidar";
import { useIsClientUserKind } from "@certa/common/hooks/useIsClientUserKind";

import type {
  CreateDataObjectWorkflowRelationActionsModelReturn,
  DataObjectInstance,
  DataObjectType
} from "@certa/queries/types/dataObjectsAPIs.types";

import { PermissionTypes } from "@certa/types";
import { useAuth } from "@certa/auth/AuthenticationProvider";

// TODO: Add tests for this hook
export const useDataObjectActionsAllowedKinds = (
  objectInstance: DataObjectInstance,
  objectType: DataObjectType,
  objectTypeWorkflowActions?: CreateDataObjectWorkflowRelationActionsModelReturn
) => {
  const { user: currentUser } = useAuth();

  const isOS3EnabledThirdPartyUser = !useIsClientUserKind();

  const { data: allKinds = [] } = useKinds({
    allowThirdParty: isOS3EnabledThirdPartyUser
  });

  const canCreateWorkflows = useCheckPermission(
    PermissionTypes.CAN_ADD_WORKFLOW
  );

  const allowedKinds = allKinds?.filter(kind => {
    const hasPermission =
      canCreateWorkflows && kind.features.includes("add_workflow");

    if (!hasPermission && !isOS3EnabledThirdPartyUser) {
      return false;
    }

    const objectTypeCurrentKindAction = objectTypeWorkflowActions?.find(
      action => action.workflowKindTag === kind.tag && !action.isDisabled
    );

    if (!objectTypeCurrentKindAction) {
      return false;
    }

    const ownerFieldEntry = Object.entries(
      objectType?.schema?.properties ?? {}
    ).find(([_, value]) => value.type === "owner");

    const ownerFieldTag = ownerFieldEntry?.[0];

    if (ownerFieldTag) {
      const restrictedOwnerGroups: string[] =
        typeof objectTypeCurrentKindAction?.restrictedForOwnerInGroup ===
        "string"
          ? [objectTypeCurrentKindAction.restrictedForOwnerInGroup]
          : (objectTypeCurrentKindAction?.restrictedForOwnerInGroup ?? []);

      const effectiveGroups = currentUser?.groups?.filter(group =>
        kind.staticGroups?.includes(group.name)
      );

      const hasUnRestrictedGroup = effectiveGroups?.some(
        group => !restrictedOwnerGroups.includes(group.name)
      );

      if (hasUnRestrictedGroup) {
        return true;
      }

      const hasRestrictedGroup = effectiveGroups?.some(group =>
        restrictedOwnerGroups.includes(group.name)
      );

      if (hasRestrictedGroup) {
        return currentUser?.email === objectInstance.data[ownerFieldTag];
      }

      return false;
    }

    return !!objectTypeCurrentKindAction;
  });

  return allowedKinds;
};
